import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { WorkMirrorStartModal } from '../components/WorkMirrorStartModal';
import { MobileNav } from '../components/MobileNav';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-4005" />;
export function WorkMirror() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return <>
      <Helmet data-id="element-4006">
        <title data-id="element-4007">
          Work Mirror — Continuous Pattern Recognition | Lazy Monk Labs
        </title>
        <meta name="description" content="Your work life repeats because your pattern repeats. Work Mirror is a continuous mirror for how you actually operate at work." data-id="element-4008" />
        <meta name="robots" content="index, follow" data-id="element-4009" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4010">
        <MobileNav data-id="element-4011" />

        <main className="pt-20 sm:pt-24 pb-12 sm:pb-16" data-id="element-4012">
          {/* 1. HERO - TIGHTENED */}
          <section className="relative py-20 sm:py-24 md:py-32 lg:py-40 px-4 sm:px-6 overflow-hidden" data-id="element-4013">
            <CoordinateGrid data-id="element-4014" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-8 sm:space-y-12" data-id="element-4015">
              <div className="space-y-6 sm:space-y-8" data-id="element-4016">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif leading-[1.1] text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4017">
                  Your work life repeats because your pattern repeats.
                </h1>

                <p className="text-lg sm:text-xl md:text-2xl text-gray-400 font-light leading-relaxed max-w-2xl" data-id="element-4018">
                  Work Mirror is a continuous mirror for how you actually
                  operate at work.
                </p>
              </div>

              <div className="pt-6 sm:pt-8 space-y-3 sm:space-y-4" data-id="element-4019">
                <button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto inline-block px-8 sm:px-12 py-4 sm:py-5 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-4020">
                  Start Work Mirror
                </button>
                <p className="text-xs sm:text-sm text-gray-600" data-id="element-4021">
                  Starts with a short calibration. Free to try.
                </p>
              </div>
            </div>
          </section>

          {/* 2. THE PROBLEM - COMPRESSED */}
          <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-4022">
            <div className="max-w-4xl mx-auto" data-id="element-4023">
              <div className="space-y-6 sm:space-y-8 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4024">
                <p data-id="element-4025">• Same conflicts, different company</p>
                <p data-id="element-4026">• Same ceilings, different role</p>
                <p data-id="element-4027">• Same loops, different year</p>
                <p data-id="element-4028">• You see other people's patterns but not your own</p>
              </div>

              <p className="text-base sm:text-lg text-[#F5F5F5] mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-[#2B2B2B]" data-id="element-4029">
                The pattern runs automatically. You only see it after it
                repeats.
              </p>
            </div>
          </section>

          {/* 3. WHAT WORK MIRROR IS - CONCEPTUAL CENTER */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-4030">
            <div className="max-w-4xl mx-auto" data-id="element-4031">
              <div className="space-y-8 sm:space-y-12" data-id="element-4032">
                <div data-id="element-4033">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5] mb-6 sm:mb-8" style={{
                  fontFamily: 'Georgia, serif'
                }} data-id="element-4034">
                    Work Mirror
                  </h2>
                  <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4035">
                    <p data-id="element-4036">
                      Work Mirror is a living mirror for how you actually
                      operate at work.
                    </p>
                    <p data-id="element-4037">
                      Not what you intend.
                      <br data-id="element-4038" />
                      Not what you believe.
                      <br data-id="element-4039" />
                      What you actually do.
                    </p>
                  </div>
                </div>

                <p className="text-base sm:text-lg text-[#F5F5F5] pt-6 sm:pt-8 border-t border-[#2B2B2B]" data-id="element-4040">
                  It shows you the pattern while it is happening, not after it
                  repeats.
                </p>
              </div>
            </div>
          </section>

          {/* 4. HOW IT WORKS - SIMPLIFIED */}
          <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-4041">
            <div className="max-w-4xl mx-auto" data-id="element-4042">
              <h2 className="text-xl sm:text-2xl font-medium text-[#F5F5F5] mb-12 sm:mb-16" data-id="element-4043">
                How it works
              </h2>

              <div className="space-y-8 sm:space-y-12" data-id="element-4044">
                <div className="space-y-3 sm:space-y-4" data-id="element-4045">
                  <p className="text-xs sm:text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-4046">
                    1. Calibration
                  </p>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-4047">
                    A short calibration establishes your baseline work pattern.
                  </p>
                </div>

                <div className="space-y-3 sm:space-y-4" data-id="element-4048">
                  <p className="text-xs sm:text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-4049">
                    2. Ongoing mirror
                  </p>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-4050">
                    You log situations and days. The mirror shows how the
                    pattern is playing out.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. WHAT THIS IS / IS NOT - COOLED */}
          <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-4051">
            <div className="max-w-4xl mx-auto" data-id="element-4052">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 sm:gap-16" data-id="element-4053">
                <div className="space-y-4 sm:space-y-6" data-id="element-4054">
                  <h3 className="text-lg sm:text-xl font-medium text-[#F5F5F5]" data-id="element-4055">
                    This is not
                  </h3>
                  <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-500" data-id="element-4056">
                    <p data-id="element-4057">• Coaching</p>
                    <p data-id="element-4058">• Therapy</p>
                    <p data-id="element-4059">• Advice</p>
                    <p data-id="element-4060">• Productivity</p>
                    <p data-id="element-4061">• Motivation</p>
                  </div>
                </div>

                <div className="space-y-4 sm:space-y-6" data-id="element-4062">
                  <h3 className="text-lg sm:text-xl font-medium text-[#F5F5F5]" data-id="element-4063">
                    This is
                  </h3>
                  <p className="text-lg sm:text-xl text-[#F5F5F5]" data-id="element-4064">A mirror.</p>
                </div>
              </div>
            </div>
          </section>

          {/* 6. THE ENGINE - COMPRESSED */}
          <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-4065">
            <div className="max-w-4xl mx-auto" data-id="element-4066">
              <div className="space-y-6 sm:space-y-8" data-id="element-4067">
                <div data-id="element-4068">
                  <p className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-3 sm:mb-4" data-id="element-4069">
                    The Engine
                  </p>
                  <p className="text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4070">
                    Work Mirror is Decoder in continuous form.
                  </p>
                </div>

                <Link to="/decoder" className="inline-flex items-center gap-3 text-sm sm:text-base text-[#F5F5F5] hover:text-gray-400 transition-colors group" data-id="element-4071">
                  <span data-id="element-4072">Learn about Decoder</span>
                  <span className="text-gray-600 group-hover:text-gray-400 transition-colors" data-id="element-4073">
                    →
                  </span>
                </Link>
              </div>
            </div>
          </section>

          {/* 7. WHO THIS IS FOR */}
          <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-4074">
            <div className="max-w-4xl mx-auto" data-id="element-4075">
              <h2 className="text-xl sm:text-2xl font-medium text-[#F5F5F5] mb-8 sm:mb-12" data-id="element-4076">
                This is for
              </h2>

              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4077">
                <p data-id="element-4078">• People who notice patterns</p>
                <p data-id="element-4079">• People whose work life keeps repeating</p>
                <p data-id="element-4080">• People who want to see the loop, not fix the symptom</p>
              </div>
            </div>
          </section>

          {/* 8. FINAL CTA - QUIET AUTHORITY */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 relative overflow-hidden border-t border-[#2B2B2B]" data-id="element-4081">
            <CoordinateGrid data-id="element-4082" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-8 sm:space-y-12" data-id="element-4083">
              <div className="space-y-6 sm:space-y-8" data-id="element-4084">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F5F5] leading-tight" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4085">
                  Start the Work Mirror
                </h2>

                <p className="text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4086">
                  The pattern is already running. This lets you see it.
                </p>
              </div>

              <div className="pt-6 sm:pt-8 space-y-3 sm:space-y-4" data-id="element-4087">
                <button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto inline-block px-8 sm:px-12 py-4 sm:py-5 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-4088">
                  Start Work Mirror
                </button>
                <p className="text-xs sm:text-sm text-gray-600" data-id="element-4089">
                  Starts with a short calibration. Free to try.
                </p>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-[#2B2B2B]" data-id="element-4090">
          <div className="max-w-6xl mx-auto" data-id="element-4091">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 sm:gap-8" data-id="element-4092">
              <div data-id="element-4093">
                <p className="text-sm text-gray-500 font-mono" data-id="element-4094">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-4095">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8 text-sm text-gray-500" data-id="element-4096">
                <a href="https://decoder-website.vercel.app/reports" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4097">
                  Decodes
                </a>
                <Link to="/mirrors/work" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4098">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4099">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4100">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4101">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-4102">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>

        {/* Work Mirror Start Modal */}
        <WorkMirrorStartModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-4103" />
      </div>
    </>;
}